import { ReactNode, useContext } from 'react';

import {
  capitalize,
  Step as MuiStep,
  StepLabel,
  StepperContext,
  StepperContextType,
  StepProps as MuiStepProps,
} from '@mui/material';
import clsx from 'clsx';

import { InternalContext } from './InternalContext';
import { Spinner } from '../../feedback';
import { Check, X } from '../../icons';

type StepColor = 'primary' | 'secondary' | 'light' | 'dark' | 'error';

type StepProps = Omit<MuiStepProps, 'content'> & {
  color?: StepColor;
  content?: ReactNode;
  label?: ReactNode;
  error?: boolean;
  loading?: boolean;
  testId?: string;
};

const wrapContent = (content: number | ReactNode) => (
  <span className="DS-Step-content">{content}</span>
);

export const Step = ({
  color = 'primary',
  className,
  content: parentContent,
  label,
  index,
  error,
  loading,
  testId = 'step',
  ...rest
}: StepProps) => {
  const stepperContext = useContext(StepperContext) as StepperContextType;
  const { size } = useContext(InternalContext);
  const content = wrapContent(parentContent ?? (index || 0) + 1);
  const isActive = stepperContext.activeStep === index;

  let child: ReactNode;
  if (label) {
    child = <StepLabel icon={content}>{label}</StepLabel>;
  } else {
    child = content;
  }
  let icon: ReactNode = (
    <Check
      className="DS-Step-statusIcon DS-Step-completedIcon"
      type="fill"
      size={size === 'medium' ? 20 : 16}
      weight="bold"
    />
  );
  if (error) {
    icon = (
      <X
        className="DS-Step-statusIcon DS-Step-errorIcon"
        size={size === 'medium' ? 20 : 16}
      />
    );
  }
  if (loading) {
    icon = (
      <Spinner
        className="DS-Step-statusIcon DS-Step-loadingIcon"
        size={size === 'medium' ? '16px' : '12px'}
        noBackground
      />
    );
    // Show spinner in icon area; still render content as the label so the step label is visible
    if (parentContent) {
      child = <StepLabel icon={content}>{label}</StepLabel>;
    }
  }

  return (
    <MuiStep
      className={clsx(
        'DS-Step-root',
        `DS-Step-color${capitalize(color)}`,
        !!rest.onClick && 'DS-Step-clickable',
        isActive && 'Mui-active',
        error && 'DS-Step-error',
        loading && 'DS-Step-loading',
        rest.disabled && 'Mui-disabled',
        className
      )}
      tabIndex={rest.disabled ? undefined : 1}
      index={index}
      data-testid={testId}
      {...rest}
    >
      {icon}
      {child}
    </MuiStep>
  );
};
